"use client";

import { useRouter } from "next/router";
import { useEffect, useRef } from "react";

/**
 * @description Component that creates an anchor point on the page and scrolls to it when the URL hash matches the anchorId.
 * 
 * @component
 * 
 * @param {object} props
 * @param {string|string[]} props.anchorId - The ID or array of IDs to match against the URL hash.
 * @param {number} [props.offsetY=0] - The vertical offset to apply when scrolling to the anchor.
 * @param {boolean} [props.offsetYLgOnly=false] - If true, applies offsetY only on large screens.
 * @param {boolean} [props.force=false] - If true, forces scrolling to the anchor even if already near it.
 * @param {function} [props.setFound=undefined] - Optional callback function that is called with true when the anchor is found.
 * @param {boolean} [props.clearHash=false] - If true, clears the URL hash after scrolling to the anchor.
 * @param {boolean} [props.instant=false] - If true, scrolls instantly without animation.
 * @param {number} [props.timeout=500] - The delay in milliseconds before performing the scroll action. 
 * 
 * @returns {*} The PageAnchor component.
 */
function PageAnchor({
  anchorId, // string or array of strings
  offsetY = 0,
  offsetYLgOnly = false,
  force = false,
  setFound = undefined,
  clearHash = false,
  instant = false,
  timeout = 500
}) {
  const router = useRouter();
  const containerRef = useRef([]);
  const anchorIds = Array.isArray(anchorId) ? anchorId : [anchorId];

  useEffect(() => {
    function scroll() {
      const hash = window.location.hash;
      if (anchorIds.includes(hash.replace('#', '')) && containerRef.current && router.isReady) {
        // found
        setFound && setFound(true);
        if (instant) {
          containerRef.current?.filter(el => el.id === hash.replace('#', ''))[0]?.scrollIntoView({ block: "end", behavior: 'instant' });

          setTimeout(() => {
            const y = containerRef.current?.filter(el => el.id === hash.replace('#', ''))[0]?.getBoundingClientRect().top + window.scrollY + offsetY;
            window.scrollTo({top: y, behavior: "instant"});
          }, 100);
        } else {
          // force scroll to get closer to element to allow the page to load before scrolling into it
          force && containerRef.current?.filter(el => el.id === hash.replace('#', ''))[0]?.scrollIntoView({ block: "end", behavior: 'instant' });

          if (offsetYLgOnly) {
            if (window.innerWidth <= 768) {
              // eslint-disable-next-line react-hooks/exhaustive-deps
              offsetY = 0; //override offsetY value only on small screens
            }
          }
      
          setTimeout(() => {
            const y = containerRef.current?.filter(el => el.id === hash.replace('#', ''))[0]?.getBoundingClientRect().top + window.scrollY + offsetY;
            window.scrollTo({top: y, behavior: "smooth"});

            setTimeout(() => {
              clearHash && window.history.replaceState(null, '', encodeURI(`${window.location.pathname}${window.location.search}`));
            }, 350);
          }, timeout);
        }
      }
    }

    scroll();
    
    window.addEventListener('hashchange', scroll);

    return () => window.removeEventListener('hashchange', scroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.asPath]);

  return (
    <>
      {anchorIds.map((id, index) => (
        <div key={id} ref={el => {
          containerRef.current[index] = el;
        }} className="h-0" id={id} />
      ))}
    </>
  )
}

export default PageAnchor;